import React, { useMemo, useState } from 'react';
import Sidebar from './Sidebar';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import type { WirelessDevice } from '@/types/deviceSetup';
import type { AuxAssign, Pump } from '@/types/dashboard';
import { apiService } from '@/utils/api';
import {
  buildWirelessBindCandidates,
  getWirelessBatteryLevelBucket,
  getWirelessBatteryPercentage,
  getWirelessBindLabel,
  isValidWirelessUuid,
  parseWirelessBindSlots,
  WIRELESS_BUTTON_UNASSIGNED_SLOT,
} from '@/utils/wirelessSwitch';

interface WirelessSwitchSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  deviceId: string;
  addon: WirelessDevice | null;
  auxAssign?: AuxAssign;
  pumpInfo?: Pump;
  onRefresh?: () => void | Promise<void>;
  onNotify?: (severity: 'success' | 'error' | 'info' | 'warning', message: string) => void;
}

const WirelessSwitchSidebar: React.FC<WirelessSwitchSidebarProps> = ({
  isOpen,
  onClose,
  deviceId,
  addon,
  auxAssign,
  pumpInfo,
  onRefresh,
  onNotify,
}) => {
  const [isWorking, setIsWorking] = useState(false);
  const [bindingIndex, setBindingIndex] = useState<number | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<number>(WIRELESS_BUTTON_UNASSIGNED_SLOT);
  const [removeOpen, setRemoveOpen] = useState(false);

  const buttonCount = Math.min(Math.max(addon?.btnType ?? 4, 1), 4);
  const candidates = useMemo(
    () => buildWirelessBindCandidates(auxAssign, pumpInfo),
    [auxAssign, pumpInfo],
  );
  const bindSlots = useMemo(
    () => parseWirelessBindSlots(addon?.bind_button, buttonCount),
    [addon?.bind_button, buttonCount],
  );
  const batteryPercent = getWirelessBatteryPercentage(addon?.battery);
  const batteryBucket = getWirelessBatteryLevelBucket(batteryPercent);
  const batteryFillWidth = {
    0: '8%',
    25: '25%',
    50: '50%',
    75: '75%',
    100: '100%',
  }[batteryBucket];

  const online = !!addon?.connected;

  const openBindDialog = (index: number) => {
    setBindingIndex(index);
    setSelectedSlot(bindSlots[index] ?? WIRELESS_BUTTON_UNASSIGNED_SLOT);
  };

  const handleBindConfirm = async () => {
    if (!addon || bindingIndex == null || !deviceId) return;

    const nextSlots = [...bindSlots];
    nextSlots[bindingIndex] = selectedSlot;
    const bind = nextSlots.join('-');

    try {
      setIsWorking(true);
      await apiService.post(`/api/v1/iot-shadow/device/${deviceId}/bindLoraButton`, {
        uuid: addon.uuid,
        bind,
      });
      onNotify?.('success', 'Wireless button binding updated');
      setBindingIndex(null);
      await onRefresh?.();
    } catch (error: any) {
      onNotify?.('error', error?.message || 'Failed to update wireless button binding');
    } finally {
      setIsWorking(false);
    }
  };

  const handleRemove = async () => {
    if (!addon || !deviceId) return;
    try {
      setIsWorking(true);
      await apiService.post(`/api/v1/iot-shadow/device/${deviceId}/removeLoraDevice`, {
        uuid: addon.uuid,
      });
      onNotify?.('success', 'Wireless switch removed');
      setRemoveOpen(false);
      onClose();
      await onRefresh?.();
    } catch (error: any) {
      onNotify?.('error', error?.message || 'Failed to remove wireless switch');
    } finally {
      setIsWorking(false);
    }
  };

  return (
    <Sidebar isOpen={isOpen} onClose={onClose} title="Wireless Switch" isLoading={isWorking}>
      {!addon || !isValidWirelessUuid(addon.uuid) ? (
        <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-900/60 px-5 py-6">
          <div className="text-sm font-medium text-gray-700 dark:text-gray-200">No wireless switch selected</div>
          <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Choose a paired wireless button from the list to view details and bindings.
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-900">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Wireless Button - {addon.uuid}
                </div>
                <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {addon.name || 'Wireless Switch'}
                </div>
              </div>
              <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium ${online ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'}`}>
                <span className={`h-2 w-2 rounded-full ${online ? 'bg-green-500' : 'bg-gray-400'}`} />
                {online ? 'Online' : 'Offline'}
              </div>
            </div>

            <div className="mt-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">Battery</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">{batteryPercent}%</span>
              </div>
              <div className="mt-2 flex items-center gap-3">
                <div className="relative h-4 flex-1 rounded-full border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 overflow-hidden">
                  <div
                    className={`h-full ${!online ? 'bg-gray-400' : batteryPercent <= 20 ? 'bg-red-500' : batteryPercent <= 50 ? 'bg-yellow-500' : 'bg-green-500'}`}
                    style={{ width: batteryFillWidth }}
                  />
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                  {online ? 'Connected' : 'Not Connected'}
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Button Bindings</div>
            {Array.from({ length: buttonCount }).map((_, index) => {
              const assignedLabel = getWirelessBindLabel(bindSlots[index] ?? WIRELESS_BUTTON_UNASSIGNED_SLOT, candidates);
              const assigned = assignedLabel.trim().length > 0;
              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => openBindDialog(index)}
                  className="w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800/80"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold ${assigned ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-300'}`}>
                        {index + 1}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          Button {index + 1}
                        </div>
                        <div className={`text-sm ${assigned ? 'text-blue-600 dark:text-blue-300' : 'text-gray-500 dark:text-gray-400'}`}>
                          {assigned ? assignedLabel : 'Not Assigned'}
                        </div>
                      </div>
                    </div>
                    <div className="text-xs text-primary-600 dark:text-primary-400">Change</div>
                  </div>
                </button>
              );
            })}
          </div>

          <button
            type="button"
            onClick={() => setRemoveOpen(true)}
            className="inline-flex items-center justify-center rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
          >
            Remove Switch
          </button>
        </div>
      )}

      <ConfirmDialog
        open={bindingIndex != null}
        title="Choose Equipment"
        onCancel={() => setBindingIndex(null)}
        onOk={handleBindConfirm}
        cancelText="Cancel"
        okText="Confirm"
        fullWidth
        maxWidth="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Select equipment to bind button {(bindingIndex ?? 0) + 1}.
          </p>
          <div className="max-h-80 overflow-y-auto space-y-2">
            <button
              type="button"
              onClick={() => setSelectedSlot(WIRELESS_BUTTON_UNASSIGNED_SLOT)}
              className={`w-full rounded-lg border px-3 py-2 text-left ${selectedSlot === WIRELESS_BUTTON_UNASSIGNED_SLOT ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' : 'border-gray-200 dark:border-gray-700'}`}
            >
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">None / Unassigned</div>
            </button>
            {candidates.length === 0 && (
              <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 px-3 py-3 text-sm text-gray-500 dark:text-gray-400">
                No timer-enabled equipment is currently available for binding.
              </div>
            )}
            {candidates.length > 0 && (
              <div className="pt-2">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Main Device
                </div>
                {candidates.map((candidate) => (
                  <button
                    key={candidate.slot}
                    type="button"
                    onClick={() => setSelectedSlot(candidate.slot)}
                    className={`mb-2 w-full rounded-lg border px-3 py-2 text-left ${selectedSlot === candidate.slot ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' : 'border-gray-200 dark:border-gray-700'}`}
                  >
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{candidate.label}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </ConfirmDialog>

      <ConfirmDialog
        open={removeOpen}
        title="Remove Wireless Switch"
        onCancel={() => setRemoveOpen(false)}
        onOk={handleRemove}
        cancelText="Cancel"
        okText="Remove"
      >
        Are you sure you want to remove wireless switch `{addon?.uuid}`?
      </ConfirmDialog>
    </Sidebar>
  );
};

export default WirelessSwitchSidebar;
